import clsx from "clsx";
import { observe } from "mobx";
import { inject, observer } from "mobx-react";
import Image from "next/image";
import { CSSProperties, RefObject, useContext, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Store } from "stores/store";
import { motion, useMotionValue, useTransform } from "framer-motion";
import ExportedImage from "next-image-export-optimizer";
import { useBackgroundQuery } from "@apollo/client";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage } from "utils/imageHelpers";
import { cn } from "@/lib/utils";
import { isOnline } from "@/constant/env";
import ChatBubble from "../web/chat/ChatBubble";
import { TrackingContext } from "../shared/tracking/Tracking";

interface ChatMessageLinkProps {
    characterImageSrc: string;
    characterImageAlt: string;
    title: string;
    link_url: string;
    link_title: string;
    altText: string;
    image_url: string;
    isMainChatMessage: boolean;
    store?: Store;
    isUser: boolean;
    disableTitle: boolean;
    timeToAppear?: Date;
    scrollRef: RefObject<HTMLDivElement>;
    onLink?: () => void;
}

const ChatMessageLink: React.FunctionComponent<ChatMessageLinkProps> = (props) => {
    if (!props.store) {
        throw Error("Store was not setup correctly");
    }

    const { chatMessageEnlarged, endOfCallRegularLocalized } = props.store;
    /* TODO: obsolete -> delete
    const [height, setHeight] = useState<number>(0);
    const [animationFinished, setAnimationFinished] = useState<boolean>(false);

    const chatMessageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (chatMessageRef.current) {
            console.log(chatMessageRef.current.clientHeight)
            setHeight(chatMessageRef.current.clientHeight);
            setAnimationFinished(true);
        }
    }, []);
*/
    const delay = props.timeToAppear ? Math.max(props.timeToAppear.getTime() - new Date().getTime(), 0) / 1000 : 0;

    // TODO: add the image markup with data from strapi

    const variantsContainer = {
        hidden: { height: 0, opacity: 0 },
        shown: {
            height: "auto",
            opacity: 1,
            transition: { duration: 0.3, delay: delay, ease: "circInOut" },
        },
        enlarged: {
            height: 0,
            opacity: 0,
            transition: { duration: 1, delay: 0 },
        },
    };

    const chatMessageRef = useRef<HTMLDivElement>(null);

    const yProgress = useMotionValue(0);
    const parentOffsetHeight = props.scrollRef.current?.offsetHeight ?? 0;

    const animate = () => {
        if (chatMessageRef.current) {
            const offsetBottom =
                parentOffsetHeight - chatMessageRef.current?.offsetTop - chatMessageRef.current?.offsetHeight;
            yProgress.set(offsetBottom + 2 * (props.scrollRef.current?.scrollTop ?? 0));
        }
        requestAnimationFrame(animate);
    };

    useEffect(() => {
        setTimeout(() => {
            animate();
        }, delay);
    }, []);

    const yProgressOpacity = useTransform(
        yProgress,
        // Map x from these values:
        [parentOffsetHeight / 2, parentOffsetHeight],
        // Into these values:
        [1, -3],
    );

    const { trackChatCTA } = useContext(TrackingContext);

    return (
        <motion.div
            variants={variantsContainer}
            initial="hidden"
            animate={chatMessageEnlarged ? "enlarged" : "shown"}
            ref={chatMessageRef}
            style={{ scale: yProgressOpacity, opacity: yProgressOpacity }}
        >
            <div onClick={() => {}}>
                <div>
                    <ChatBubble
                        title={!props.disableTitle ? (props.isUser ? "HEUTE-ICH" : "ZUKUNFTS-ICH") : ""}
                        alignLeft={!props.isUser}
                        alignRight={props.isUser}
                        header={
                            <div className="flex items-center gap-2">
                                <div className="h-[25px] w-[25px] shrink-0 bg-[url('data:image/png;base64,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')] bg-cover"></div>
                                <span className="font-bold">{props.title}</span>
                            </div>
                        }
                    >
                        <div
                            className={cn("block overflow-hidden", "select-none", {
                                //"pointer-events-none": !isOnline,
                                "4k:rounded-[30px] rounded-[7px] border-[1px] border-whiteShade-30 shadow-[0_0_13px_0_rgba(255,255,255,0.3)]":
                                    isOnline,
                            })}
                            onClick={() => {
                                props.onLink && props.onLink();
                                trackChatCTA("inchatlink");
                            }}
                            // target="blank"
                            title={props.link_title}
                            // href={`https://${props.link_url}`}
                        >
                            <div className={cn({ "shadow-[inset_0_0_26px_6.6px_rgba(255,255,255,0.6)]": isOnline })}>
                                <div
                                    className={cn("4k:gap-16 flex gap-2", {
                                        "4k:p-12 p-3 shadow-[inset_0_0.66px_5pxpx_0_rgba(255,255,255,0.35)]": isOnline,
                                    })}
                                >
                                    <ExportedImage
                                        // src={
                                        //     "https://neueleben-strapi.curious.zone/uploads/Placeholder_QR_Code_3514040426.png"
                                        // }
                                        // alt={props.altText}
                                        src={
                                            isOnline
                                                ? props.image_url
                                                : endOfCallRegularLocalized?.qrCode !== undefined &&
                                                    endOfCallRegularLocalized?.qrCode !== null
                                                  ? getImageUrlFromGeneralImage(endOfCallRegularLocalized.qrCode)
                                                  : "No image provided"
                                        }
                                        alt={
                                            isOnline
                                                ? props.altText
                                                : endOfCallRegularLocalized?.qrCode !== undefined &&
                                                    endOfCallRegularLocalized?.qrCode !== null
                                                  ? getAltTextFromGeneralImage(endOfCallRegularLocalized.qrCode)
                                                  : "No image provided"
                                        }
                                        fill={true}
                                        className={cn("4k:h-[260px] 4k:w-[260px] 4k:rounded-[32px] relative h-8 w-8", {
                                            "shadow-[0_0_15px_0_rgba(0,0,0,0.4)]": isOnline,
                                        })}
                                    />
                                    <div className="flex flex-col justify-center">
                                        <div className="font-bold">{props.link_title}</div>
                                        <div className="4k:text-[38px] text-[10px]">{props.link_url}</div>
                                    </div>
                                    {isOnline && (
                                        <div className="ml-auto flex items-center pl-2">
                                            <svg width="18" height="19" viewBox="0 0 18 19" fill="none">
                                                <rect
                                                    x="0.5"
                                                    y="0.838867"
                                                    width="17"
                                                    height="17"
                                                    rx="8.5"
                                                    stroke="white"
                                                />
                                                <path
                                                    d="M5.5 9.33887H12.5M12.5 9.33887L9 5.83887M12.5 9.33887L9 12.8389"
                                                    stroke="white"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </ChatBubble>
                </div>
            </div>
        </motion.div>
    );
};

export default inject("store")(observer(ChatMessageLink));
